import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import API_URL from '../../config/api';
import LineSelection from './LineSelection';
import ModelSelection from './ModelSelection';
import RetouchSelection from './RetouchSelection';

const WorkerInterface = () => {
  const { logout, user } = useAuth();
  const [step, setStep] = useState('line');
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [counters, setCounters] = useState(() => {
    // Récupérer les compteurs depuis localStorage
    const saved = localStorage.getItem('qualityCounters');
    return saved ? JSON.parse(saved) : {};
  });

  // Sauvegarder les compteurs dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('qualityCounters', JSON.stringify(counters));
  }, [counters]);

  // Synchroniser les compteurs avec les enregistrements réels quand on arrive sur la page retouch
  useEffect(() => {
    const syncCounters = async () => {
      if (step === 'retouch' && selectedModel && selectedLine && user?.id) {
        try {
          // Récupérer tous les enregistrements pour ce modèle et cette chaîne
          const response = await axios.get(`${API_URL}/api/quality-records/`, {
            params: {
              production_line: selectedLine.id,
              model: selectedModel.id,
            }
          });
          
          const records = response.data.results || response.data || [];
          
          // Filtrer par worker actuel
          const userRecords = records.filter(record => {
            const workerId = typeof record.worker === 'object' ? record.worker?.id : record.worker;
            return workerId === user.id;
          });
          
          // Compter par type de retouche
          const realCounters = {};
          userRecords.forEach(record => {
            const retouchId = typeof record.retouch_type === 'object' ? record.retouch_type?.id : record.retouch_type;
            if (retouchId) {
              realCounters[retouchId] = (realCounters[retouchId] || 0) + 1;
            }
          });
          
          // Mettre à jour les compteurs seulement si différents
          setCounters(prev => {
            const hasChanges = Object.keys(realCounters).some(key => prev[key] !== realCounters[key]) ||
                             Object.keys(prev).some(key => !realCounters[key] && prev[key] > 0);
            
            if (hasChanges) {
              return realCounters;
            }
            return prev;
          });
        } catch (error) {
          console.error('Erreur lors de la synchronisation des compteurs:', error);
          // En cas d'erreur, on garde les compteurs localStorage
        }
      }
    };
    
    syncCounters();
  }, [step, selectedModel, selectedLine, user]);

  const handleLineSelect = (line) => {
    setSelectedLine(line);
    setSelectedModel(null);
    setCounters({});
    localStorage.removeItem('qualityCounters');
    setStep('model');
  };

  const handleModelSelect = (model) => {
    setSelectedModel(model);
    setCounters({});
    localStorage.removeItem('qualityCounters');
    setStep('retouch');
  };

  const handleRetouchClick = async (retouch) => {
    try {
      await axios.post(`${API_URL}/api/quality-records/`, {
        production_line: selectedLine.id,
        model: selectedModel.id,
        retouch_type: retouch.id,
      });

      const key = `${retouch.id}`;
      setCounters(prev => ({
        ...prev,
        [key]: (prev[key] || 0) + 1
      }));

      const button = document.querySelector(`[data-retouch-id="${retouch.id}"]`);
      if (button) {
        button.classList.add('clicked');
        setTimeout(() => button.classList.remove('clicked'), 300);
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleRetouchQuantitySubmit = async (retouch, quantity) => {
    try {
      const promises = [];
      for (let i = 0; i < quantity; i++) {
        promises.push(
          axios.post(`${API_URL}/api/quality-records/`, {
            production_line: selectedLine.id,
            model: selectedModel.id,
            retouch_type: retouch.id,
          })
        );
      }
      await Promise.all(promises);

      const key = `${retouch.id}`;
      setCounters(prev => ({
        ...prev,
        [key]: (prev[key] || 0) + quantity
      }));

      const button = document.querySelector(`[data-retouch-id="${retouch.id}"]`);
      if (button) {
        button.classList.add('clicked');
        setTimeout(() => button.classList.remove('clicked'), 500);
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleBack = () => {
    if (step === 'retouch') {
      setStep('model');
      setSelectedModel(null);
    } else if (step === 'model') {
      setStep('line');
      setSelectedLine(null);
    }
  };

  const handleReset = () => {
    setStep('line');
    setSelectedLine(null);
    setSelectedModel(null);
    setCounters({});
    localStorage.removeItem('qualityCounters');
  };

  const handleRetouchDecrease = async (retouch) => {
    const key = `${retouch.id}`;
    const currentCount = counters[key] || 0;
    
    if (currentCount > 0) {
      try {
        // Récupérer les enregistrements pour cette retouche, filtrés par worker et triés par date décroissante
        const response = await axios.get(`${API_URL}/api/quality-records/`, {
          params: {
            production_line: selectedLine.id,
            model: selectedModel.id,
            retouch_type: retouch.id,
            ordering: '-recorded_at'
          }
        });
        
        // Gérer la pagination de l'API
        let records = [];
        if (response.data.results) {
          records = response.data.results;
        } else if (Array.isArray(response.data)) {
          records = response.data;
        }
        
        // Filtrer par worker actuel pour ne supprimer que ses propres enregistrements
        // Le worker peut être un ID (number) ou un objet avec un id
        if (user && user.id) {
          records = records.filter(record => {
            const workerId = typeof record.worker === 'object' ? record.worker?.id : record.worker;
            return workerId === user.id;
          });
        }
        
        // Synchroniser le compteur avec le nombre réel d'enregistrements
        const realCount = records.length;
        if (realCount === 0) {
          // Aucun enregistrement réel, réinitialiser le compteur
          setCounters(prev => ({
            ...prev,
            [key]: 0
          }));
          return;
        }
        
        // Si le compteur est supérieur au nombre réel, le corriger
        if (currentCount > realCount) {
          setCounters(prev => ({
            ...prev,
            [key]: realCount
          }));
        }
        
        // Supprimer le dernier enregistrement (le plus récent)
        const recordToDelete = records[0];
        
        if (!recordToDelete || !recordToDelete.id) {
          alert('Enregistrement invalide');
          // Synchroniser le compteur
          setCounters(prev => ({
            ...prev,
            [key]: realCount
          }));
          return;
        }
        
        try {
          await axios.delete(`${API_URL}/api/quality-records/${recordToDelete.id}/`);
          
          // Mettre à jour le compteur seulement après succès
          setCounters(prev => ({
            ...prev,
            [key]: Math.max(0, (prev[key] || 0) - 1)
          }));
        } catch (deleteError) {
          console.error('Erreur lors de la suppression:', deleteError);
          
          // Si l'enregistrement n'existe plus (404), synchroniser le compteur
          if (deleteError.response?.status === 404) {
            // L'enregistrement a déjà été supprimé, synchroniser avec le nombre réel
            setCounters(prev => ({
              ...prev,
              [key]: realCount
            }));
            // Pas besoin d'alerte, juste synchroniser silencieusement
            return;
          }
          
          const errorMessage = deleteError.response?.data?.detail || 
                              deleteError.response?.data?.error || 
                              deleteError.message || 
                              'Erreur lors de la suppression';
          
          if (deleteError.response?.status === 403) {
            alert('Vous n\'avez pas la permission de supprimer cet enregistrement');
          } else {
            alert(`Erreur: ${errorMessage}`);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des enregistrements:', error);
        const errorMessage = error.response?.data?.detail || 
                            error.response?.data?.error || 
                            error.message || 
                            'Erreur lors de la diminution du compteur';
        alert(`Erreur: ${errorMessage}`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header professionnel */}
      <header className="bg-white border-b border-gray-200 shadow-corporate sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between h-auto sm:h-16 py-3 sm:py-0 gap-3 sm:gap-0">
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/logo.png" 
                alt="Logo Codersa" 
                className="w-10 h-10 object-contain flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="w-10 h-10 bg-gradient-primary rounded-lg items-center justify-center shadow-corporate hidden">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-corporate-900">Contrôle de Qualité de Codersa</h1>
                <p className="text-xs text-gray-500">
                  {step === 'line' && 'Sélectionnez une chaîne'}
                  {step === 'model' && selectedLine && `Chaîne: ${selectedLine.name}`}
                  {step === 'retouch' && selectedModel && `Modèle: ${selectedModel.name}`}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {step !== 'line' && (
                <button
                  onClick={handleBack}
                  className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-4 py-2.5 rounded-lg font-semibold text-sm shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 flex-1 sm:flex-none"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Retour</span>
                </button>
              )}
              <button
                onClick={handleReset}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold text-sm shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 flex-1 sm:flex-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span>Accueil</span>
              </button>
              <button
                onClick={logout}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2.5 rounded-lg font-semibold text-sm shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 flex-1 sm:flex-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {step === 'line' && <LineSelection onSelect={handleLineSelect} />}
          {step === 'model' && selectedLine && (
            <ModelSelection
              productionLine={selectedLine}
              onSelect={handleModelSelect}
            />
          )}
          {step === 'retouch' && selectedModel && (
            <RetouchSelection
              model={selectedModel}
              counters={counters}
              onRetouchClick={handleRetouchClick}
              onRetouchQuantitySubmit={handleRetouchQuantitySubmit}
              onRetouchDecrease={handleRetouchDecrease}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default WorkerInterface;
